import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@artosera/ui';
import type { Langue } from '@artosera/shared';
import type { OffrePresentee } from '../../api/offres.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { LigneEcart, LigneEcheance, PhraseOffre, TexteProchaineAction } from './OffreResume.js';
import { prochaineAction, type ContexteAction, type ProchaineAction } from './syntheseOffre.js';
import styles from './OffreCarte.module.css';

export function tonEtatOffre(offre: OffrePresentee) {
  if (offre.echue) return 'reserved';
  switch (offre.etat) {
    case 'acceptee':
      return 'available';
    case 'envoyee':
      return 'accent';
    case 'brouillon':
      return 'neutral';
    default:
      return 'withdrawn';
  }
}
/** Le bouton de la carte : l'action à faire, ou rien quand on attend ou que c'est fini. */
const ACTION_PARAMETRE: Partial<Record<ProchaineAction, string>> = {
  envoyer: '?action=envoyer',
  relancer: '?action=relancer',
  echue: '?action=relancer',
};
const BOUTON: Partial<Record<ProchaineAction, string>> = {
  envoyer: 'envoyer',
  relancer: 'relancer',
  echue: 'relancer',
  convertir: 'convertir',
  reserver: 'reserver',
};

export function OffreCarte({
  offre,
  galerieSlug,
  langue,
  contexte,
}: {
  offre: OffrePresentee;
  galerieSlug: string;
  langue: Langue;
  contexte: ContexteAction;
}) {
  const { t } = useTranslation('regie');
  const navigate = useNavigate();
  const action = prochaineAction(offre, contexte);
  const bouton = BOUTON[action];
  const adresse = `/${galerieSlug}/offres/${offre.id}`;
  return (
    <article
      className={styles.carte}
      data-attente={action === 'attendre' ? 'true' : undefined}
      data-action={bouton ? 'true' : undefined}
    >
      <div className={styles.corps}>
        <header className={styles.entete}>
          <Monogramme nom={offre.contactNom} taille={32} />
          <Link className={styles.numero} to={adresse}>
            {offre.numero}
          </Link>
          <Badge tone={tonEtatOffre(offre)}>{t(`offres.etats.${offre.etat}`)}</Badge>
          {offre.echue ? <Badge tone="reserved">{t('offres.echue')}</Badge> : null}
          {offre.canal && offre.canal !== 'courriel' ? (
            <span className={styles.canal}>{t(`offres.canaux.${offre.canal}`)}</span>
          ) : null}
        </header>
        <PhraseOffre offre={offre} langue={langue} />
        <LigneEcart offre={offre} langue={langue} />
        <LigneEcheance offre={offre} langue={langue} />
      </div>
      <footer className={styles.pied}>
        <TexteProchaineAction offre={offre} action={action} langue={langue} />
        {bouton ? (
          <Button
            className={styles.bouton}
            aria-label={t('offres.carte.boutonPour', {
              action: t(`offres.carte.bouton.${bouton}`),
              numero: offre.numero,
            })}
            onClick={() => navigate(`${adresse}${ACTION_PARAMETRE[action] ?? ''}`)}
          >
            {t(`offres.carte.bouton.${bouton}`)}
          </Button>
        ) : null}
      </footer>
    </article>
  );
}
